// Shared TanStack Query keys + provider config.
//
// Keep keys here so cache-invalidation after mutation is a one-liner.

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry not-implemented stubs or auth failures.
        const status =
          (error as { status?: number } | undefined)?.status ?? 0;
        if (status === 401 || status === 403 || status === 501) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

export const queryKeys = {
  customers: {
    all: ['customers'] as const,
    list: (params?: Record<string, unknown>) =>
      ['customers', 'list', params ?? {}] as const,
    byId: (id: string) => ['customers', id] as const,
  },
  orders: {
    all: ['orders'] as const,
    list: (params?: Record<string, unknown>) =>
      ['orders', 'list', params ?? {}] as const,
    byId: (id: string) => ['orders', id] as const,
    refunds: (id: string) => ['orders', id, 'refunds'] as const,
    disputes: (id: string) => ['orders', id, 'disputes'] as const,
  },
  refunds: {
    all: ['refunds'] as const,
    list: (params?: Record<string, unknown>) =>
      ['refunds', 'list', params ?? {}] as const,
  },
  disputes: {
    all: ['disputes'] as const,
    list: (params?: Record<string, unknown>) =>
      ['disputes', 'list', params ?? {}] as const,
  },
  payouts: {
    all: ['payouts'] as const,
    list: (params?: Record<string, unknown>) =>
      ['payouts', 'list', params ?? {}] as const,
  },
  reports: {
    summary: (params?: Record<string, unknown>) =>
      ['reports', 'summary', params ?? {}] as const,
  },
  auditFeed: {
    list: (params?: Record<string, unknown>) =>
      ['audit_feed', 'list', params ?? {}] as const,
  },
  subscriptions: {
    all: ['subscriptions'] as const,
    list: (params?: Record<string, unknown>) =>
      ['subscriptions', 'list', params ?? {}] as const,
    byId: (id: string) => ['subscriptions', id] as const,
  },
  products: {
    all: ['products'] as const,
    list: () => ['products', 'list'] as const,
    bySlug: (slug: string) => ['products', slug] as const,
  },
  skus: {
    all: ['skus'] as const,
    list: (productSlug?: string) =>
      ['skus', 'list', productSlug ?? null] as const,
    byId: (id: string) => ['skus', id] as const,
  },
  bundles: {
    all: ['bundles'] as const,
    list: () => ['bundles', 'list'] as const,
    byId: (id: string) => ['bundles', id] as const,
  },
  coupons: {
    all: ['coupons'] as const,
    list: () => ['coupons', 'list'] as const,
    byId: (id: string) => ['coupons', id] as const,
  },
  influencers: {
    all: ['influencers'] as const,
    list: (params?: Record<string, unknown>) =>
      ['influencers', 'list', params ?? {}] as const,
    byId: (id: string) => ['influencers', id] as const,
  },
  emailTemplates: {
    all: ['email_templates'] as const,
    list: () => ['email_templates', 'list'] as const,
    byId: (id: string) => ['email_templates', id] as const,
  },
  emailSequences: {
    all: ['email_sequences'] as const,
    list: () => ['email_sequences', 'list'] as const,
    byId: (id: string) => ['email_sequences', id] as const,
  },
  emailSends: {
    all: ['email_sends'] as const,
    list: (params?: Record<string, unknown>) =>
      ['email_sends', 'list', params ?? {}] as const,
  },
  audit: {
    all: ['audit'] as const,
    listFor: (subjectType: string, subjectId: string) =>
      ['audit', subjectType, subjectId] as const,
  },
  catalog: {
    preview: () => ['catalog', 'preview'] as const,
    publicCurrent: () => ['catalog', 'current'] as const,
  },
  settings: {
    users: () => ['settings', 'users'] as const,
    channels: () => ['settings', 'channels'] as const,
    apiKeys: () => ['settings', 'api-keys'] as const,
    runtimeMode: () => ['settings', 'runtime-mode'] as const,
    runtimeAudit: () => ['settings', 'runtime-mode', 'audit'] as const,
    modeCounts: () => ['settings', 'mode-counts'] as const,
  },
} as const;
